(function () {
  "use strict";

  const BASE_URL = "https://seymour-fit-finder-backup.worlton-0754.chatgpt.site";
  const ENDPOINT = BASE_URL + "/api/events";
  const TOKEN_ENDPOINT = BASE_URL + "/api/session";
  const STATE_KEY = "ss_fit_finder_state_v1";
  const ATTEMPT_KEYS = [
    "fit_case_id", "case_started_at", "case_updated_at", "equipment_type", "input_method",
    "manufacturer_input", "model_input", "original_model_query", "normalized_manufacturer",
    "normalized_model", "measurement_value", "measurement_unit", "measurement_mm",
    "recommendation_record_id", "recommended_filter_size", "recommended_variant_ids",
    "model_recommended_filter_size", "model_variant_ids", "measurement_recommended_filter_size",
    "selected_variant_id", "match_result", "outcome", "optional_scope_prompt_shown",
    "optional_scope_provided", "product_click", "cart_linked", "order_reference", "fit_feedback",
    "entry_point", "product_handle"
  ];
  const EVENT_TYPES = {
    fit_finder_opened: "opened",
    fit_finder_device_selected: "equipment_selected",
    fit_finder_model_searched: "search",
    fit_finder_model_no_match: "search",
    fit_finder_optional_equipment_provided: "search",
    fit_finder_od_submitted: "measurement",
    fit_finder_model_match_found: "recommendation",
    fit_finder_measurement_outcome: "recommendation",
    fit_finder_match_found: "recommendation",
    fit_finder_results_fallback: "recommendation",
    fit_finder_recommendation_conflict: "recommendation",
    fit_finder_no_match: "no_match",
    fit_finder_model_result_clicked: "product_click",
    fit_finder_cart_linked: "cart",
    fit_finder_availability_email_clicked: "availability_email"
  };
  const tokenPromises = new Map();

  function identifier(prefix) {
    if (window.crypto && typeof window.crypto.randomUUID === "function") return prefix + "_" + window.crypto.randomUUID();
    return prefix + "_" + Date.now() + "_" + Math.random().toString(36).slice(2, 14);
  }

  function safeText(value) {
    if (typeof value !== "string") return null;
    const cleaned = value.replace(/[\u0000-\u001f\u007f]/g, " ").trim();
    return cleaned || null;
  }

  function safeNumber(value) {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  function normalize(value) {
    const text = safeText(value);
    return text ? text.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim().replace(/\s+/g, " ") : null;
  }

  function readState() {
    let state = {};
    try { state = JSON.parse(window.localStorage.getItem(STATE_KEY) || "{}"); } catch (error) {}
    if (!state || typeof state !== "object" || Array.isArray(state)) state = {};
    if (!safeText(state.fit_session_id)) {
      state.fit_session_id = identifier("ffsession");
      state.created_at = Date.now();
      state.updated_at = Date.now();
      try { window.localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch (error) {}
    }
    return state;
  }

  function writeState(state) {
    state.updated_at = Date.now();
    try { window.localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch (error) {}
    return state;
  }

  function startCase(seed) {
    const previous = readState();
    const next = { ...previous };
    ATTEMPT_KEYS.forEach(function (key) { delete next[key]; });
    next.fit_case_id = identifier("ffcase");
    next.case_started_at = Date.now();
    next.case_updated_at = Date.now();
    Object.assign(next, seed || {});
    writeState(next);
    try { window.dispatchEvent(new CustomEvent("ss-fit-finder-case-started", { detail: next })); } catch (error) {}
    return next;
  }

  function ensureCase(state) {
    if (safeText(state.fit_case_id)) return state;
    return startCase({ entry_point: safeText(state.entry_point) });
  }

  function equipmentType(state, data) {
    const allowed = ["Telescope", "Binocular", "Finder", "Spotting scope", "Camera lens", "Other"];
    if (allowed.includes(state.equipment_type)) return state.equipment_type;
    return data.device_selected === "binoculars" ? "Binocular" : null;
  }

  function inputMethod(state, eventName) {
    if (["make_model", "measurement", "both"].includes(state.input_method)) return state.input_method;
    if (eventName === "fit_finder_model_searched" || eventName === "fit_finder_model_no_match") return "make_model";
    if (eventName === "fit_finder_optional_equipment_provided") return "both";
    if (["fit_finder_od_submitted", "fit_finder_match_found", "fit_finder_measurement_outcome", "fit_finder_no_match"].includes(eventName)) {
      return state.original_model_query ? "both" : "measurement";
    }
    return null;
  }

  function outcomeValue(state, eventName, data) {
    const allowed = ["model_match", "unknown_model", "measurement_match", "no_size_match", "conflict", "unavailable"];
    if (allowed.includes(data.outcome)) return data.outcome;
    if (eventName === "fit_finder_model_no_match") return "unknown_model";
    if (eventName === "fit_finder_no_match") return "no_size_match";
    if (eventName === "fit_finder_recommendation_conflict") return "conflict";
    if (eventName === "fit_finder_model_match_found") return Number(data.available_options || 0) > 0 ? "model_match" : "unavailable";
    return allowed.includes(state.outcome) ? state.outcome : null;
  }

  function matchResult(state, eventName, outcome) {
    if (outcome === "unknown_model") return "measurement_required";
    if (outcome === "no_size_match") return "no_match";
    if (outcome === "conflict") return "conflict";
    if (["model_match", "measurement_match", "unavailable"].includes(outcome || "")) return "matched";
    if (["matched", "measurement_required", "no_match", "conflict"].includes(state.match_result)) return state.match_result;
    return null;
  }

  function mappedEventType(eventName, data) {
    if (eventName === "fit_finder_result_clicked") return data.result_type === "support_cta" ? "availability_email" : "product_click";
    if (eventName === "fit_finder_measurement_outcome" && data.outcome === "no_size_match") return "no_match";
    return EVENT_TYPES[eventName] || null;
  }

  function makePayload(eventName, data) {
    const eventType = mappedEventType(eventName, data);
    if (!eventType) return null;
    const state = ensureCase(readState());
    const rawModel = safeText(data.model_query) || safeText(data.model) || safeText(state.model_input) || safeText(state.original_model_query);
    const rawManufacturer = safeText(data.manufacturer) || safeText(state.manufacturer_input);
    const measurementValue = safeNumber(data.od_input_value) ?? safeNumber(state.measurement_value);
    const measurementMm = safeNumber(data.od_normalized_mm) ?? safeNumber(state.measurement_mm);
    const unit = data.od_input_unit === "in" || data.od_input_unit === "mm"
      ? data.od_input_unit
      : state.measurement_unit === "in" || state.measurement_unit === "mm" ? state.measurement_unit : null;
    const outcome = outcomeValue(state, eventName, data);
    const modelSize = safeText(data.model_recommended_size) || safeText(state.model_recommended_filter_size)
      || (eventName === "fit_finder_model_match_found" ? safeText(data.recommended_size) : null);
    const measurementSize = safeText(data.measurement_recommended_size) || safeText(state.measurement_recommended_filter_size)
      || (["fit_finder_measurement_outcome", "fit_finder_match_found", "fit_finder_no_match"].includes(eventName)
        ? safeText(data.recommended_size) || safeText(data.matched_variant_title) || safeText(state.recommended_filter_size)
        : null);
    const recommendationSize = outcome === "conflict" ? measurementSize
      : safeText(data.recommended_size) || safeText(data.matched_variant_title) || safeText(state.recommended_filter_size) || modelSize;
    const recommendationRecordId = safeText(data.record_id) || safeText(data.recommendation_record_id) || safeText(state.recommendation_record_id);
    const variantId = safeText(data.variant_id) || safeText(data.matched_variant_id) || safeText(state.selected_variant_id);
    const result = matchResult(state, eventName, outcome);
    const productClick = eventType === "product_click" || state.product_click === true;
    const cartLinked = eventType === "cart" || state.cart_linked === true;
    const conflict = outcome === "conflict" || result === "conflict";
    const optionCount = safeNumber(data.available_options) ?? safeNumber(data.results_shown_count) ?? safeNumber(state.available_option_count);

    return {
      event_id: identifier("ffevt"),
      event_timestamp: new Date().toISOString(),
      schema_version: "2",
      fit_session_id: safeText(state.fit_session_id),
      fit_case_id: safeText(state.fit_case_id),
      event_type: eventType,
      outcome: outcome,
      equipment_type: equipmentType(state, data),
      input_method: inputMethod(state, eventName),
      manufacturer_input: rawManufacturer,
      model_input: rawModel,
      normalized_manufacturer: safeText(state.normalized_manufacturer) || normalize(rawManufacturer),
      normalized_model: safeText(state.normalized_model) || normalize(rawModel),
      measurement_value: measurementValue,
      measurement_unit: unit,
      measurement_mm: measurementMm,
      recommendation_record_id: recommendationRecordId,
      recommended_filter_size: recommendationSize,
      model_recommended_filter_size: modelSize,
      measurement_recommended_filter_size: measurementSize,
      recommended_variant_id: variantId,
      match_result: result,
      optional_scope_prompt_shown: eventName === "fit_finder_optional_equipment_prompt_shown" || state.optional_scope_prompt_shown === true,
      optional_scope_provided: state.optional_scope_provided === true,
      product_click: productClick,
      cart_linked: cartLinked,
      order_reference: safeText(state.order_reference),
      fit_feedback: ["fit_confirmed", "did_not_fit", "not_provided"].includes(state.fit_feedback) ? state.fit_feedback : null,
      source_path: window.location.pathname,
      entry_point: safeText(data.entry_point) || safeText(state.entry_point),
      available_option_count: optionCount === null ? null : Math.trunc(optionCount),
      product_handle: safeText(data.product_handle) || safeText(state.product_handle),
      conflict_flag: conflict
    };
  }

  function tokenFor(payload) {
    const key = payload.fit_session_id + ":" + payload.fit_case_id;
    if (!tokenPromises.has(key)) {
      const query = new URLSearchParams({ fit_session_id: payload.fit_session_id, fit_case_id: payload.fit_case_id });
      const promise = fetch(TOKEN_ENDPOINT + "?" + query.toString(), {
        method: "GET", mode: "cors", credentials: "omit", cache: "no-store"
      }).then(function (response) {
        if (!response.ok) throw new Error("token_unavailable");
        return response.json();
      }).then(function (body) {
        if (!body || !body.token) throw new Error("token_unavailable");
        return body.token;
      }).catch(function () {
        tokenPromises.delete(key);
        return null;
      });
      tokenPromises.set(key, promise);
    }
    return tokenPromises.get(key);
  }

  function send(eventName, data) {
    try {
      const payload = makePayload(eventName, data || {});
      if (!payload) return;
      tokenFor(payload).then(function (token) {
        if (!token) return;
        fetch(ENDPOINT, {
          method: "POST",
          mode: "cors",
          credentials: "omit",
          keepalive: true,
          headers: { "Content-Type": "application/json", "X-Fit-Finder-Token": token },
          body: JSON.stringify(payload)
        }).catch(function () {});
      });
    } catch (error) {}
  }

  window.SeymourFitFinderBackup = Object.freeze({ send: send, startCase: startCase });
})();
